import { ACCOUNT } from '@src/domain/constants'
import { IAccountInfo } from '@src/domain/interfaces/IAccount'
import { getAccountInfoApi } from '@src/infra/api/accounts'
import { ability, defineAbilityFor } from '@src/utility/ability'
import { getCookie } from '@src/utility/Utils'
import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useMemo, useReducer, type FC, type ReactNode } from 'react'

// =================================================================================
export enum DispatchType {
  UPDATE_ANY_TYPE = 'UPDATE_ANY_TYPE',
  SET_BRACKET_FULLSIZE = 'SET_BRACKET_FULLSIZE'
}

type InitialState = {
  accountInfo?: IAccountInfo
  isLoadingUser: boolean
  isAuthenticated: boolean
  isBracketFullsize: boolean
}
type UpdateAnyType = { type: DispatchType.UPDATE_ANY_TYPE; payload: InitialState }
type SetBracketFullsize = { type: DispatchType.SET_BRACKET_FULLSIZE; payload: boolean }

type ActionType = UpdateAnyType | SetBracketFullsize

const INITIAL_STATE = {
  accountInfo: undefined,
  isLoadingUser: true,
  isAuthenticated: false,
  isBracketFullsize: false
}

interface ContextProps {
  state: InitialState
  // eslint-disable-next-line no-unused-vars
  dispatch: (args: ActionType) => void
  refetchAccount: () => void
}

const AppContext = createContext<ContextProps>({
  state: INITIAL_STATE,
  dispatch: () => { },
  refetchAccount: () => { }
})

const reducer = (state: InitialState, action: ActionType) => {
  switch (action.type) {
    case DispatchType.UPDATE_ANY_TYPE:
      return { ...state, ...action.payload }
    case DispatchType.SET_BRACKET_FULLSIZE:
      return { ...state, isBracketFullsize: action.payload }

    default: {
      return state
    }
  }
}

// =======================================================
type AppProviderProps = { children: ReactNode }
// =======================================================

export const AppProvider: FC<AppProviderProps> = ({ children }) => {
  const [stateData, dispatch] = useReducer(reducer, INITIAL_STATE)

  const { data: accountInfo, isLoading: isLoadingUser, refetch: refetchAccountInfo } = useQuery<IAccountInfo, Error>({
    queryKey: [ACCOUNT.ACTION_TYPES.GET_ACCOUNT_INFO],
    cacheTime: 5000,
    queryFn: () => {
      return getAccountInfoApi()
    },
    onSuccess(data: any) {
      return data
    },
    retry: false,
    enabled: !Boolean(stateData.accountInfo)
  })

  const refetchAccount = () => {
    return refetchAccountInfo()
  }

  const isAuthenticated = () => {
    const isHasCookie = Boolean(getCookie('_Gidu_'))
    return Boolean(isHasCookie || (!isLoadingUser && accountInfo && accountInfo.id))
  }

  useEffect(() => {
    dispatch({
      type: DispatchType.UPDATE_ANY_TYPE,
      payload: {
        accountInfo,
        isLoadingUser,
        isAuthenticated: isAuthenticated()
      } as InitialState
    })
    ability.update(defineAbilityFor(accountInfo?.roleCode).rules)
  }, [accountInfo, isLoadingUser])

  useEffect(() => {
    const onFullChange = () => {
      const isFs = !!document.fullscreenElement
      // cập nhật state context
      dispatch({ type: DispatchType.SET_BRACKET_FULLSIZE, payload: isFs })
    }

    document.addEventListener("fullscreenchange", onFullChange)
    document.addEventListener("webkitfullscreenchange", onFullChange) // cho Safari cũ

    return () => {
      document.removeEventListener("fullscreenchange", onFullChange)
      document.removeEventListener("webkitfullscreenchange", onFullChange)
    }
  }, [])

  const contextValue = useMemo(() => ({ state: stateData, dispatch, refetchAccount }),
    [stateData, dispatch, refetchAccount])

  return <AppContext.Provider value={contextValue}>
    {children}
  </AppContext.Provider >
}

export const useAppContext = () => useContext<ContextProps>(AppContext)

export default AppContext
